
import { useEffect, useRef, useState } from 'react';
import { NewSelectMultiSelect } from '../../PIM/common';
import type { BombiixCellRendererParams, BombiixRowId } from '../types';
import closeMenuOnAnchorScroll from './closeMenuOnAnchorScroll';

interface SelectOption {
  label?: string;
  name?: string;
  value?: unknown;
  _id?: unknown;
}

interface SelectInputCellExtras {
  options?: SelectOption[];
  getOptionLabel?: (opt: SelectOption) => string;
  getOptionValue?: (opt: SelectOption) => string;
  isClearable?: boolean;
  isMulti?: boolean;
  /** Renders an in-menu search field at the top of the option list (forwarded to NewSelectMultiSelect). */
  showSearch?: boolean;
  /** Placeholder for the in-menu search field (defaults to "Search"). */
  searchPlaceholder?: string;
  /** Renders a "Select All" checkbox row above the option list. Multi-select only. */
  selectAllLabel?: string;
  /** Renders a "Clear All" footer button under the option list. Omit to hide it. */
  clearAllLabel?: string;
  /** Disables individual options (react-select native): the option renders but cannot be selected or de-selected. */
  isOptionDisabled?: (_option: SelectOption) => boolean;
  /** Fired with the locked options when the user tries to remove one (forwarded to NewSelectMultiSelect). */
  onLockedRemoval?: (_lockedOptions: SelectOption[]) => void;
  /** When set, renders a trailing "| +" add button that invokes this with the cell's field name. */
  onAdd?: (field: string) => void;
  /** Accessible/tooltip label for the add button (defaults to "Add"). */
  addLabel?: string;
  /**
   * Multi-select only. Asked on every pick made while the menu is open, which the cell otherwise
   * buffers until the menu closes. Resolving true writes the pick to the row at once, so a change
   * that needs a confirmation raises it on the click instead of on close or on save.
   */
  onMultiPick?: (_rowId: BombiixRowId, _field: string, _value: SelectOption[]) => Promise<boolean>;
  /** Label of the option the menu opens scrolled to while nothing is selected (forwarded to NewSelectMultiSelect). */
  scrollToLabel?: string;
}

type SelectInputCellProps = BombiixCellRendererParams & SelectInputCellExtras;

/** Tallest the portalled option list may grow before it scrolls internally (px). */
const MENU_MAX_HEIGHT = 220;

const portalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
  /* Inside a fixed-height AG Grid cell the multi-select chips must NOT wrap: wrapping
     onto a clipped second line makes react-select scroll its hidden input into view on
     each pick, which snaps the value container left and hides the newest chip. Force a
     single non-wrapping row that scrolls horizontally instead. Mirrors the base
     valueContainer padding from NewSelectMultiSelect so alignment is preserved (this
     override replaces, not merges with, that base rule). */
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    padding: '0 0 0 10px',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    overflowY: 'hidden',
  }),
};

/**
 * Dropdown cell backed by NewSelectMultiSelect (react-select under the hood).
 * Reflects the choice optimistically so the selected label appears on the same
 * click instead of waiting on the parent's recalc round-trip. When the stored
 * row value is a full object (e.g. a colourway from DB), extract the comparable
 * key via valueFn before searching.
 */
function SelectInputCell(params: SelectInputCellProps) {
  const {
    value,
    data,
    colDef,
    options = [],
    getOptionLabel,
    getOptionValue,
    onRowChange,
    getRowId,
    placeholder,
    disabled,
    isClearable = false,
    isMulti = false,
    showSearch,
    searchPlaceholder,
    selectAllLabel,
    clearAllLabel,
    isOptionDisabled,
    onLockedRemoval,
    error,
    onAdd,
    addLabel,
    onMultiPick,
    scrollToLabel,
  } = params;

  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;
  const labelFn = getOptionLabel || ((opt: SelectOption) => String(opt?.label ?? opt?.name ?? ''));
  const valueFn = getOptionValue || ((opt: SelectOption) => String(opt?.value ?? opt?._id ?? ''));
  /* Comparable key list for a multi value, whether it holds option objects or bare ids. */
  const keysOf = (raw: unknown): string =>
    (Array.isArray(raw) ? raw : [])
      .map((entry) => (entry !== null && typeof entry === 'object' ? valueFn(entry as SelectOption) : String(entry)))
      .join('\u0000');

  const [localValue, setLocalValue] = useState<unknown>(value);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  /** The in-flight scroll-pin frame, so only one loop ever writes scrollLeft. See preserveGridScroll. */
  const pinFrameRef = useRef<number | null>(null);
  /* Multi-select picks are held here and written to the row once, when the menu closes. Committing
     on every pick re-runs the host's row state, which rebuilds the column defs and force-refreshes
     the grid — that remounts this control and tears the open menu off its cell. The PIM grid's
     multi-select cell commits on close for the same reason. */
  const pendingMultiRef = useRef<SelectOption[] | null>(null);
  /* Whether the option list is actually open. Buffering only applies to picks made INSIDE the menu:
     a chip's ✕ and Clear All are reachable with the menu shut, and those fire no close event, so
     buffering them would strand the edit and the row would silently keep the removed value. */
  const menuOpenRef = useRef(false);
  const [, setMenuPlacementTick] = useState(0);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(
    () => () => {
      if (pinFrameRef.current !== null) cancelAnimationFrame(pinFrameRef.current);
    },
    [],
  );

  let selected: SelectOption | SelectOption[] | null;
  if (isMulti) {
    /* Multi-select value is an array of option objects (or comparable keys). Map
       each entry back to a full option so react-select renders the chips even
       when the stored row value is a bare-key array. */
    const arr: unknown[] = Array.isArray(localValue) ? localValue : [];
    selected = arr
      .map((entry) => {
        if (entry !== null && typeof entry === 'object') {
          const key = valueFn(entry as SelectOption);
          return options.find((opt) => valueFn(opt) === key) ?? (entry as SelectOption);
        }
        return options.find((opt) => valueFn(opt) === String(entry)) ?? null;
      })
      .filter((opt): opt is SelectOption => Boolean(opt));
  } else {
    const valueKey =
      localValue !== null && typeof localValue === 'object'
        ? valueFn(localValue as SelectOption)
        : localValue !== undefined && localValue !== null
          ? String(localValue)
          : undefined;
    selected = options.find((opt) => valueFn(opt) === valueKey) ?? null;
  }

  /* Deliberately no grid-scroll handling here. This cell used to pin the grid's horizontal
     scrollLeft for a dozen frames after focus and after every pick, to undo the browser scrolling
     the control into view. react-select places a portalled `menuPosition: 'fixed'` menu ONCE, from
     the control's rect as it opens, and never tracks it again — so scrolling the grid back slid the
     control out from under its own menu, which is the detached dropdown BM-9620 reports, and it
     re-ran on every pick, which is why it took several values to become obvious. The PIM grid's
     multi-select cell never touches scroll and never detaches. Letting the browser's own scroll
     stand is what keeps the control and its menu in agreement. */
  const handleChange = (opt: SelectOption | ReadonlyArray<SelectOption> | null) => {
    const next = opt ?? null;
    setLocalValue(next);
    if (isMulti && menuOpenRef.current) {
      const picked = Array.isArray(next) ? [...next] : [];
      pendingMultiRef.current = picked;
      commitPickWhenAsked(onMultiPick, rowId, field, picked, () => pendingMultiRef.current === picked, () => {
        pendingMultiRef.current = null;
        if (rowId !== undefined && field) onRowChange?.(rowId, field, picked);
      });
      return;
    }
    pendingMultiRef.current = null;
    if (rowId !== undefined && field) onRowChange?.(rowId, field, isMulti && !Array.isArray(next) ? [] : next);
  };

  const commitPendingMulti = () => {
    const pending = pendingMultiRef.current;
    pendingMultiRef.current = null;
    if (!pending || rowId === undefined || !field) return;
    if (keysOf(pending) === keysOf(value)) return;
    onRowChange?.(rowId, field, pending);
  };

  /* Commit on menu close ONLY, never on blur. react-select emits a blur between picks whenever the
     control input remounts, and committing there wrote a half-finished selection to the row — the
     very mid-interaction re-render this change exists to avoid. Every way the menu can close routes
     through onMenuClose (the wrapper defers its own blur close and calls it), so nothing is lost. */
  const handleMenuOpen = () => {
    menuOpenRef.current = true;
  };

  const handleMenuClose = () => {
    menuOpenRef.current = false;
    commitPendingMulti();
  };

  /* When the cell has a trailing "+" add button, the unified field border is drawn by the
     wrapper (.cag-cell--with-add) so the control + button read as ONE bordered box with a
     divider (matching the "Text Box | %" design). Strip react-select's own border/radius here
     so it doesn't double up inside that wrapper. */
  const selectStyles = onAdd
    ? {
        ...portalStyles,
        control: (base: Record<string, unknown>) => ({ ...base, border: 'none', borderRadius: 0, boxShadow: 'none' }),
      }
    : portalStyles;

  const selectProps = {
    options,
    value: selected,
    onChange: handleChange,
    onMenuOpen: handleMenuOpen,
    onMenuClose: handleMenuClose,
    getOptionLabel: labelFn,
    getOptionValue: valueFn,
    placeholder: placeholder ?? colDef?.headerName ?? 'Dropdown',
    isDisabled: Boolean(disabled),
    isClearable,
    isMulti,
    /* Master-design menu extras (react-select wrapper handles the open-state so clicking these
       doesn't blur-close the menu): in-menu search, "Select All" (multi only), "Clear All". Each
       renders only when its prop is passed, so columns opt in per-cell via cellProps. Spread
       conditionally so undefined values are omitted (exactOptionalPropertyTypes). */
    ...(showSearch !== undefined ? { showSearch } : {}),
    ...(showSearch ? { isSearchable: false } : {}),
    ...(searchPlaceholder !== undefined ? { searchPlaceholder } : {}),
    ...(selectAllLabel !== undefined ? { selectAllLabel } : {}),
    ...(clearAllLabel !== undefined ? { clearAllLabel } : {}),
    ...(isOptionDisabled !== undefined ? { isOptionDisabled } : {}),
    ...(onLockedRemoval !== undefined ? { onLockedRemoval } : {}),
    ...(scrollToLabel !== undefined ? { scrollToLabel } : {}),
    menuPosition: 'fixed' as const,
    menuPlacement: 'auto' as const,
    styles: selectStyles,
    invalid: Boolean(error),
  };

  /* AG Grid cells have overflow: hidden — portal the menu to <body> so it isn't
     clipped. `menuPortalTarget` + `menuShouldBlockScroll` are react-select props
     forwarded verbatim by NewSelectMultiSelect's ...rest spread, but they're
     not on its Props interface, so pass them through an untyped bag. */
  const hasMenuExtras = Boolean(showSearch || selectAllLabel || clearAllLabel);
  const portalProps: Record<string, unknown> = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    /* Cap the option list so a long master (e.g. 40+ label values) cannot run past the bottom of the
       grid. Passed as react-select's own prop rather than a `menuList` style: NewSelectMultiSelect
       merges consumer styles shallowly, so a `menuList` override replaced its padding, column and
       gap, and the options slid left of the Select All row. */
    maxMenuHeight: MENU_MAX_HEIGHT,
    /* menuShouldBlockScroll installs a document-level scroll lock. With menu extras (search /
       Select All) the user interacts INSIDE the open menu — that scroll lock intercepts the
       interaction and blur-closes the menu on the first click into the search box or Select All
       row. Disable it when extras are present. */
    menuShouldBlockScroll: !hasMenuExtras,
    closeMenuOnScroll: closeMenuOnAnchorScroll({
      anchor: wrapRef,
      isOpen: () => menuOpenRef.current,
      onFollow: () => setMenuPlacementTick((tick) => tick + 1),
    }),
  };

  const handleAdd = () => {
    if (field && onAdd) onAdd(field);
  };

  /* Chips are pinned to one non-wrapping row, so past two or three picks the rest are scrolled out
     of sight with nothing to indicate they exist. Surface the full selection on hover. */
  const selectedTitle = Array.isArray(selected)
    ? selected.map((opt) => labelFn(opt)).filter(Boolean).join(', ')
    : selected
      ? labelFn(selected)
      : '';

  return (
    <div ref={wrapRef} className={`cag-cell-wrap${error ? ' cag-cell-wrap--error' : ''}`}>
      <div className="cag-cell">
        <div
          className={`cag-select-field${
            onAdd ? ` cag-cell--with-add${disabled ? ' cag-cell--with-add-disabled' : ''}${error ? ' cag-cell--with-add-error' : ''}` : ''
          }`}
          {...(selectedTitle ? { title: selectedTitle } : {})}
        >
          <NewSelectMultiSelect<SelectOption> {...selectProps} {...portalProps} />
          {onAdd ? (
            <button
              type="button"
              className="cag-cell-add-btn"
              onClick={handleAdd}
              disabled={Boolean(disabled)}
              aria-label={addLabel ?? 'Add'}
              title={addLabel ?? 'Add'}
            >
              <span className="icon-Add-bold" />
            </button>
          ) : null}
        </div>
      </div>
      {error ? <span className="cag-cell-error">{error}</span> : null}
    </div>
  );
}

export default SelectInputCell;

/**
 * Ask the host whether a buffered multi-select pick must be written at once, and write it when the
 * answer is yes and the pick is still the latest one. Does nothing when the host asks nothing.
 * @param ask the host's question, from the column's `onMultiPick`
 * @param rowId the row the pick belongs to
 * @param field the field the pick writes
 * @param picked the selection as it stands after the pick
 * If the host's check fails the answer is taken as "not now": the pick stays buffered and is written
 * when the menu closes, exactly as it would be without a host, and the save-time check still runs.
 * @param isCurrent whether no later pick has replaced this one
 * @param commit writes the pick to the row
 * @returns resolves once the pick has been written or left buffered; it never rejects
 */
async function commitPickWhenAsked(
  ask: SelectInputCellExtras['onMultiPick'],
  rowId: BombiixRowId | undefined,
  field: string | undefined,
  picked: SelectOption[],
  isCurrent: () => boolean,
  commit: () => void,
): Promise<void> {
  if (!ask || rowId === undefined || !field) return;
  const commitNow = await ask(rowId, field, picked).catch(() => false);
  if (commitNow && isCurrent()) commit();
}